'use client'

import { useAtomValue, useSetAtom } from 'jotai'
import { useCallback } from 'react'

import { authAtom, authDialogAtom } from '@/state/auth'

/**
 * Auth가 필요한 액션을 감싸는 훅.
 *
 * 인증된 상태면 바로 실행하고,
 * 미인증 상태면 로그인 Dialog를 열고 로그인 성공 후 자동 실행합니다.
 *
 * @example
 * const { requireAuth } = useAuthGuard()
 *
 * // 버튼 클릭 시
 * <button onClick={() => requireAuth(() => handleExecuteOrder())}>
 *   Execute Order
 * </button>
 *
 * // async 액션도 동일하게 사용 가능
 * requireAuth(async () => {
 *   await createOrder(params)
 * })
 */
export function useAuthGuard() {
  const { isAuthenticated, accessToken } = useAtomValue(authAtom)
  const setAuthDialog = useSetAtom(authDialogAtom)

  const isLoggedIn = isAuthenticated && Boolean(accessToken)

  const requireAuth = useCallback(
    (action: () => void) => {
      if (isLoggedIn) {
        action()
        return
      }

      setAuthDialog({ open: true, pendingAction: action })
    },
    [isLoggedIn, setAuthDialog]
  )

  return { requireAuth, isLoggedIn }
}
